// Unpublished Work © 2023 Deere & Company.
import { Popover, PopoverButton, PopoverContent } from "@deere/fuel-react";
import CircleIcon from "@mui/icons-material/Circle";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import { Box, Divider, ListItemButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import AllInvoicesNotification from "./components/FileDownloadedNotification/AllInvoicesNotification";
import ConsolidatedInvoicesNotification from "./components/FileDownloadedNotification/ConsolidatedInvoicesNotification";
import OrdersDownloadNotification from "./components/FileDownloadedNotification/OrdersDownloadNotification";
import PromotionNotification from "./components/FileDownloadedNotification/PromotionsNotification";
import ReturnsExcelNotification from "./components/FileDownloadedNotification/ReturnsExcelNotification";
import NoNotificationsAvailable from "./components/NoNotificationsAvailable/NoNotificationsAvailable";
import ReturnsNotification from "./components/ReturnsNotification/ReturnsNotification";
import useNotifications, {
  Notification,
} from "./hooks/useNotifications/useNotifications";
import { ConfigContext } from "../../../../../../context/ConfigContext";
import useJobsDownloadNotifier from "../../../../../../hooks/useJobsDownloadNotifier/useJobsDownloadNotifier";
import usePromotionDownloadNotifier from "../../../../../../hooks/usePromotionDownloadNotifier/usePromotionDownloadNotifier";
import {
  hasNeedActionReturnAvailable,
  hasReturnNotification,
  useReturnsNotifier,
} from "../../../../../../hooks/useReturnsNotifier/useReturnsNotifier";
import {
  ALL_ORDERS_DOWNLOAD,
  INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION,
  INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION,
  INVOICE_CONSOLIDATION,
  INVOICE_FILTER,
  NOTIFICATIONS_TYPE_TO_DELETE,
  ORDERS_BY_LINE_ITEM_DOWNLOAD,
  PROMOTION_PARTS_DETAIL_DOWNLOAD,
  RETURN_SUMMARY_EXCEL,
  RETURNS_EXCEL,
} from "../../../../../../utils/constants";
import { downloadFileWithUrl } from "../../../../../../utils/downloadFileWithUrl/downloadFileWithUrl";
import { INV_DOWNLOAD_ID } from "../../../../../../utils/eventConstants";

const Notifications = ({ accountId }: { accountId: string }) => {
  const theme = useTheme();
  const { NUMBER_OF_NOTIFICATIONS_TO_SHOW = 30, DELETE_EXPIRED_NOTIFICATIONS } =
    useContext(ConfigContext);
  const { t } = useTranslation("common");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const {
    notifications,
    areNewNotificationsAvailable,
    markAllNotificationAsRead,
    expireNotification,
    deleteNotification,
  } = useNotifications({ accountId, isMenuOpen });

  const { needActionCount, returnNotificationSeen, setReturnNotificationSeen } =
    useReturnsNotifier();

  usePromotionDownloadNotifier(accountId);
  useJobsDownloadNotifier(accountId);

  const getExpireNotification = (type: string) =>
    NOTIFICATIONS_TYPE_TO_DELETE.includes(type) && DELETE_EXPIRED_NOTIFICATIONS
      ? deleteNotification
      : expireNotification;

  const getNotification = (
    filename: string,
    notification: Notification,
    type?: string
  ) => {
    const expireNotificationFn = getExpireNotification(
      notification.payload.type ?? ""
    );
    if (type === ORDERS_BY_LINE_ITEM_DOWNLOAD) {
      return (
        <OrdersDownloadNotification
          notification={notification}
          accountId={accountId}
          downloadFile={downloadFileWithUrl}
          expireNotification={expireNotificationFn}
          successMessage={t(
            "notifications.orderByLineItem.download.success.notification"
          )}
          failureMessage={t(
            "notifications.orderByLineItem.download.failure.message"
          )}
        />
      );
    }

    if (
      (type === INVOICE_CONSOLIDATION ||
        type === INVOICE_FILTER ||
        type === INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION ||
        type === INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION ||
        filename?.startsWith(INV_DOWNLOAD_ID)) &&
      type !== PROMOTION_PARTS_DETAIL_DOWNLOAD
    ) {
      return (
        <ConsolidatedInvoicesNotification
          notification={notification}
          accountId={accountId}
          expireNotification={expireNotificationFn}
          downloadFile={downloadFileWithUrl}
        />
      );
    }

    if (type === "Invoice") {
      return (
        <AllInvoicesNotification
          notification={notification}
          accountId={accountId}
          expireNotification={expireNotificationFn}
          downloadFile={downloadFileWithUrl}
        />
      );
    }

    if (type === ALL_ORDERS_DOWNLOAD) {
      return (
        <OrdersDownloadNotification
          notification={notification}
          accountId={accountId}
          expireNotification={expireNotificationFn}
          downloadFile={downloadFileWithUrl}
          successMessage={t(
            "notifications.allOrders.download.success.notification",
            { fileName: notification.payload.filename || "All Orders" }
          )}
          failureMessage={t(
            "notifications.allOrders.download.failure.message",
            { fileName: notification.payload.filename || "All Orders" }
          )}
        />
      );
    }

    if (type === RETURNS_EXCEL || type === RETURN_SUMMARY_EXCEL) {
      return (
        <ReturnsExcelNotification
          notification={notification}
          accountId={accountId}
          expireNotification={expireNotificationFn}
          downloadFile={downloadFileWithUrl}
        />
      );
    }

    if (type === PROMOTION_PARTS_DETAIL_DOWNLOAD) {
      return (
        <PromotionNotification
          notification={notification}
          downloadFile={downloadFileWithUrl}
          expireNotification={expireNotificationFn}
        />
      );
    }

    return null;
  };

  return (
    <Popover
      showClose={false}
      isOpen={isMenuOpen}
      onClose={() => setIsMenuOpen(false)}
    >
      <PopoverButton
        data-testid="notification-button"
        aria-label="Notification"
        variant="text"
        sx={{
          ":hover": {
            background: theme.tokens.FuelColorGray50,
          },
          ":active": {
            background: theme.tokens.FuelColorBackgroundActionSecondaryPressed,
          },
        }}
        onClick={() => {
          setIsMenuOpen((prevState) => !prevState);
        }}
      >
        <Box sx={{ position: "relative", lineHeight: "0" }}>
          {(areNewNotificationsAvailable ||
            hasReturnNotification(needActionCount, returnNotificationSeen)) && (
            <CircleIcon
              data-testid="notification-bubble-icon"
              sx={{
                color: theme.tokens.FuelColorAlertInfoIcon,
                position: "absolute",
                borderRadius: theme.tokens.FuelBorderRadiusFull,
                backgroundColor: theme.tokens.FuelColorBackgroundWhite,
                top: "11%",
                right: "11%",
                fontSize: "0.5rem",
              }}
            />
          )}
          <NotificationsOutlinedIcon
            sx={{
              color: theme.tokens.FuelColorTextIcon,
            }}
            data-testid="notification-icon"
            onClick={() => {
              markAllNotificationAsRead();
              setReturnNotificationSeen(true);
            }}
          />
        </Box>
      </PopoverButton>
      <PopoverContent data-testid="notification-menu">
        <Box width="340px">
          <Typography
            data-testid="notification-header"
            sx={{
              fontWeight: 600,
              p: 2,
              pl: 5,
              height: "40px",
              fontSize: "14px",
              color: theme.tokens.FuelColorTextTertiary,
            }}
          >
            {t("header.notificationMenu.header")}
          </Typography>
        </Box>
        <Divider />
        <Box height="340px" width="420px" sx={{ overflowY: "auto" }}>
          {notifications.length === 0 &&
          !hasNeedActionReturnAvailable(needActionCount) ? (
            <ListItemButton sx={{ height: "100%" }}>
              <NoNotificationsAvailable key="no-notifications-available" />
            </ListItemButton>
          ) : (
            <>
              {[...notifications]
                .reverse()
                .slice(0, NUMBER_OF_NOTIFICATIONS_TO_SHOW)
                .map((notification) => {
                  const { filename, id, type } = notification.payload;

                  return (
                    <ListItemButton key={`${filename}=${id}`}>
                      {getNotification(filename, notification, type)}
                    </ListItemButton>
                  );
                })}
              {hasNeedActionReturnAvailable(needActionCount) && (
                <ListItemButton key="return-need-action">
                  <ReturnsNotification needActionCount={needActionCount} />
                </ListItemButton>
              )}
            </>
          )}
        </Box>
      </PopoverContent>
    </Popover>
  );
};

export default Notifications;
